import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import {
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  BanknotesIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  CheckIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [activeTab, setActiveTab] = useState('recents');
  const [serviceFilter, setServiceFilter] = useState('');
  const [showServiceFilter, setShowServiceFilter] = useState(false);
  const [showYearFilter, setShowYearFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [stripeBalance, setStripeBalance] = useState(null);
  const PAGE_SIZE = 50;
  const { user } = useAuth();
  const { organismeActif, isSuperAdminMode } = useOrganisme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }

    const currentYear = new Date().getFullYear();
    setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
    
    fetchPendingPayments();
    fetchStripeBalance();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, organismeActif, isSuperAdminMode]);

  useEffect(() => {
    fetchPendingPayments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, organismeActif, isSuperAdminMode]);

  const fetchStripeBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const org = (!isSuperAdminMode && organismeActif) ? organismeActif : 'SAR';
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/stripe-account/balance?organisme=${org}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStripeBalance({
        amount:  res.data.available?.[0]?.amount ?? null,
        pending: res.data.pending?.[0]?.amount ?? null,
        organisme: org,
      });
    } catch {
      setStripeBalance(null);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedYear) {
        params.append('annee', selectedYear);
      }
      if (!isSuperAdminMode && organismeActif) {
        params.append('organisme', organismeActif);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/payment/pending?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des paiements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'non_demande':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ClockIcon className="h-3 w-3 mr-1" />
            Non demandé
          </span>
        );
      case 'demande':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            Demandé
          </span>
        );
      case 'paye':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Payé
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant || 0);
  };

  const statutPaiementLabel = (typePaiement) => {
    switch (typePaiement) {
      case 'en_ligne':
        return 'Payé en ligne';
      case 'cheque':
        return 'Payé en chèque';
      case 'espece':
        return 'Payé en espèces';
      case 'virement':
        return 'Payé par virement';
      case 'gratuit':
        return 'Gratuit';
      default:
        return 'Payé';
    }
  }; 

  // Filtrer les données par service
  const filteredAdhesions = data?.adhesionsEnAttente || [];
  
  let filteredServices = data?.servicesEnAttente || [];
  if (serviceFilter) {
    filteredServices = filteredServices.filter(s => s.typeService === serviceFilter);
  }
  
  let filteredPaiements = data?.paiementsRecents || [];
  if (serviceFilter) {
    filteredPaiements = filteredPaiements.filter(p =>
      p.typeService === serviceFilter ||
      (p.type === 'virement_unaf' && serviceFilter === 'assurance_unaf') ||
      (p.type === 'adhesion' && (
        (serviceFilter === 'miellerie'      && p.organisme === 'AMAIR') ||
        (serviceFilter === 'assurance_unaf' && p.organisme === 'SAR')
      ))
    );
  }

  const totalPages = Math.ceil(filteredPaiements.length / PAGE_SIZE);
  const paginatedPaiements = filteredPaiements.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const exportToExcel = () => {
    if (!data?.paiementsRecents?.length) return;

    const source = serviceFilter ? filteredPaiements : data.paiementsRecents;

    // Transactions entrantes (adhésions + services)
    const rowsPaiements = source
      .filter((item) => item.type !== 'virement_unaf' && item.type !== 'virement_stripe')
      .map((item) => {
        const frais = item.stripeFee ?? item.frais ?? null;
        // Pour les paiements Stripe, le net est stripeNet ou montant-frais.
        // Pour les autres (chèque, espèces…), le montant reçu = montant.
        const net = item.stripeNet != null
          ? item.stripeNet
          : frais != null
            ? (item.montant || 0) - frais
            : item.montant || 0;
        return {
          'Date': item.datePaiement ? new Date(item.datePaiement).toLocaleDateString('fr-FR') : '-',
          'Type': item.type === 'adhesion' ? 'Adhésion' : 'Service',
          'Description': item.type === 'adhesion'
            ? `${item.organisme} — ${item.user?.prenom || ''} ${item.user?.nom || ''}`
            : `${item.nom || 'Service'} — ${item.user?.prenom || ''} ${item.user?.nom || ''}`,
          'Montant (€)': item.montant || 0,
          'Frais (€)': frais != null ? frais : '',
          'Net (€)': net,
          'Statut': statutPaiementLabel(item.typePaiement),
        };
      });

    // Virements UNAF (sortants)
    const rowsVirements = source
      .filter((item) => item.type === 'virement_unaf' || item.type === 'virement_stripe')
      .map((item) => ({
        'Date': item.datePaiement ? new Date(item.datePaiement).toLocaleDateString('fr-FR') : '-',
        'Type': item.type === 'virement_unaf' ? 'Virement UNAF' : 'Virement Stripe',
        'Description': item.type === 'virement_unaf'
          ? `Export UNAF ${item.annee} — ${item.nombrePaiements} adhérent${item.nombrePaiements > 1 ? 's' : ''} · SAR`
          : `${item.description || 'Virement vers le compte bancaire'} · ${item.organisme}`,
        'Montant (€)': -(item.montant || 0),
        'Frais (€)': '',
        'Net (€)': -(item.montant || 0),
        'Statut': { paid: 'Viré', in_transit: 'En transit', pending: 'En attente', failed: 'Échoué', canceled: 'Annulé' }[item.payoutStatus] || item.payoutStatus || '-',
      }));

    const excelData = [...rowsPaiements, ...rowsVirements].sort(
      (a, b) => new Date(b['Date'].split('/').reverse().join('-')) - new Date(a['Date'].split('/').reverse().join('-'))
    );

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

    const colWidths = [
      { wch: 14 },
      { wch: 16 },
      { wch: 48 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
      { wch: 20 },
    ];
    worksheet['!cols'] = colWidths;

    // Générer le nom du fichier
    const today = new Date();
    const dateStr = today.toLocaleDateString('fr-FR').replace(/\//g, '').slice(0, 6);
    const organisme = user?.organisme || 'ALL';
    const fileName = `${organisme}_paiements_${selectedYear}_${dateStr}.xlsx`;

    // Télécharger le fichier
    XLSX.writeFile(workbook, fileName);
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <span className="font-medium text-gray-900">Administration</span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>Paiements</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Suivi des paiements</h1>
            <p className="mt-2 text-sm text-gray-600">
              Vue d'ensemble des paiements en attente et récents
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isSuperAdminMode && (
              <HelpButton 
                title={helpContent.payments.title}
                sections={helpContent.payments.sections}
              />
            )}
            <button
              onClick={fetchPendingPayments}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 pb-8 space-y-6">
        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Filtre par type de service */}
          <div className="relative">
            <button
              onClick={() => {
                setShowServiceFilter(!showServiceFilter);
                setShowYearFilter(false);
              }}
              className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Service
              {serviceFilter && (
                <span className="ml-2 px-2 py-0.5 bg-honey-500 rounded-full text-xs">
                  {serviceFilter === 'miellerie' ? 'Miellerie' : 'UNAF'}
                </span>
              )}
            </button>
            
            {showServiceFilter && (
              <div className="absolute z-10 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-900">Type de service</span>
                    {serviceFilter && (
                      <button
                        onClick={() => {
                          setServiceFilter('');
                          setShowServiceFilter(false);
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {(() => {
                      const services = [];
                      if (organismeActif === 'AMAIR') {
                        services.push({ value: 'miellerie', label: 'Miellerie' });
                      }
                      if (organismeActif === 'SAR') {
                        services.push({ value: 'assurance_unaf', label: 'Services de l\'UNAF' });
                      }
                      if (isSuperAdminMode) {
                        services.push(
                          { value: 'miellerie', label: 'Miellerie' },
                          { value: 'assurance_unaf', label: 'Services de l\'UNAF' }
                        );
                      }
                      return services.map((svc) => (
                        <button
                          key={svc.value}
                          onClick={() => { setServiceFilter(svc.value); setShowServiceFilter(false); setCurrentPage(1); }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                            serviceFilter === svc.value
                              ? 'bg-honey-50 text-honey-700'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <span>{svc.label}</span>
                          {serviceFilter === svc.value && (
                            <CheckIcon className="h-4 w-4 text-honey-600" />
                          )}
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filtre par année */}
          <div className="relative">
            <button
              onClick={() => {
                setShowYearFilter(!showYearFilter);
                setShowServiceFilter(false);
              }}
              className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Année
              {selectedYear ? (
                <span className="ml-2 px-2 py-0.5 bg-indigo-500 rounded-full text-xs">
                  {selectedYear}
                </span>
              ) : (
                <span className="ml-2 px-2 py-0.5 bg-gray-500 rounded-full text-xs">
                  Toutes
                </span>
              )}
            </button>
            
            {showYearFilter && (
              <div className="absolute z-10 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-900">Année</span>
                    {selectedYear && (
                      <button
                        onClick={() => {
                          setSelectedYear(null);
                          setShowYearFilter(false);
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Effacer
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {availableYears.map((annee) => (
                      <button
                        key={annee}
                        onClick={() => { setSelectedYear(annee); setShowYearFilter(false); }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedYear === annee
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <span>{annee}</span>
                        {selectedYear === annee && (
                          <CheckIcon className="h-4 w-4 text-indigo-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-1 gap-4 ${
          [stripeBalance !== null, isSuperAdminMode || organismeActif === 'SAR'].filter(Boolean).length === 2
            ? 'md:grid-cols-3 xl:grid-cols-3'
            : [stripeBalance !== null, isSuperAdminMode || organismeActif === 'SAR'].some(Boolean)
              ? 'md:grid-cols-3 xl:grid-cols-3'
              : 'md:grid-cols-3 xl:grid-cols-4'
        }`}>
          {stripeBalance !== null && (
            <div className="bg-white rounded-lg shadow p-4 border border-indigo-100">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-indigo-100 rounded-lg">
                  <BanknotesIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Fonds Stripe
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stripeBalance.amount !== null ? `${stripeBalance.amount} €` : '—'}
                  </p>
                  {stripeBalance.pending !== null && parseFloat(stripeBalance.pending) > 0 && (
                    <p className="text-xs text-indigo-500 mt-0.5">
                      + {stripeBalance.pending} € à venir
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg">
                <BanknotesIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total reçu {selectedYear} (tous moyens de paiements)</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatMontant(data?.stats?.montantPaiementsRecus || 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-slate-100 rounded-lg">
                <CreditCardIcon className="h-6 w-6 text-slate-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Frais Stripe {selectedYear}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatMontant(data?.stats?.totalFrais || 0)}
                </p>
              </div>
            </div>
          </div>
          {(isSuperAdminMode || organismeActif === 'SAR') && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-honey-100 rounded-lg">
                  <ArrowsRightLeftIcon className="h-6 w-6 text-honey-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Virements UNAF {selectedYear}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatMontant(data?.stats?.totalVirementsUnaf || 0)}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-honey-100 rounded-lg">
                <CreditCardIcon className="h-6 w-6 text-honey-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">En attente de paiement</p>
                <p className="text-2xl font-bold text-gray-900">{formatMontant((data?.stats?.montantAdhesionsEnAttente || 0) + (data?.stats?.montantServicesEnAttente || 0))}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex items-center justify-between">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => { setActiveTab('recents'); setCurrentPage(1); }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recents'
                  ? 'border-slate-700 text-slate-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Transactions ({filteredPaiements.length})
            </button>
            <button
              onClick={() => { setActiveTab('adhesions'); setCurrentPage(1); }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'adhesions'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Adhésions en attente ({filteredAdhesions.length})
            </button>
            <button
              onClick={() => { setActiveTab('services'); setCurrentPage(1); }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'services'
                  ? 'border-honey-500 text-honey-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Services en attente ({filteredServices.length})
            </button>
          </nav>
          
          {/* Bouton Export Excel - visible uniquement sur l'onglet paiements reçus */}
          {activeTab === 'recents' && filteredPaiements.length > 0 && (
            <button
              onClick={exportToExcel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Exporter Excel
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {activeTab === 'adhesions' && (
            <>
              {filteredAdhesions.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Adhérent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organisme
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date inscription
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAdhesions.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                              <UserIcon className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {item.user?.prenom} {item.user?.nom}
                              </div>
                              <div className="text-sm text-gray-500">{item.user?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.organisme === 'ADAR' ? 'bg-blue-50 text-[#0B4395]' :
                            item.organisme === 'SAR' ? 'bg-green-50 text-green-700' : 
                            'bg-pink-50 text-pink-700'
                          }`}>
                            {item.organisme}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatMontant(item.montant)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(item.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(item.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => navigate(`/adherents/${item._id}`)}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune adhésion en attente</h3>
                  <p className="mt-1 text-sm text-gray-500">Toutes les adhésions ont été réglées.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'services' && (
            <>
              {filteredServices.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Adhérent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Caution
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredServices.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-honey-100 rounded-full flex items-center justify-center">
                              <UserIcon className="h-5 w-5 text-honey-500" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {item.user?.prenom} {item.user?.nom}
                              </div>
                              <div className="text-sm text-gray-500">{item.user?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.nom}</div>
                          <div className="text-sm text-gray-500">{item.typeService}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatMontant(item.montant)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(item.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.cautionStatus === 'recu' ? 'bg-green-100 text-green-800' :
                            item.cautionStatus === 'rendu' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.cautionStatus === 'recu' ? 'Reçue' :
                             item.cautionStatus === 'rendu' ? 'Rendue' : 'En attente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => navigate(`/service/${item._id}`)}
                            className="text-honey-600 hover:text-honey-900 transition-colors"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun service en attente</h3>
                  <p className="mt-1 text-sm text-gray-500">Tous les services ont été réglés.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'recents' && (
            <>
              {filteredPaiements.length > 0 ? (
                <div className="p-6 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-500 border-b border-slate-100">
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium">Type</th>
                        <th className="pb-3 font-medium">Description</th>
                        <th className="pb-3 font-medium text-right">Montant</th>
                        <th className="pb-3 font-medium text-right">Frais</th>
                        <th className="pb-3 font-medium text-right">Net</th>
                        <th className="pb-3 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedPaiements.map((item, index) => {
                        if (item.type === 'virement_unaf' || item.type === 'virement_stripe') {
                          const payoutStatusBadge = {
                            paid:       { cls: 'bg-green-100 text-green-700',  label: 'Viré' },
                            in_transit: { cls: 'bg-blue-100 text-blue-700',    label: 'En transit' },
                            pending:    { cls: 'bg-yellow-100 text-yellow-700', label: 'En attente' },
                            failed:     { cls: 'bg-red-100 text-red-700',       label: 'Échoué' },
                            canceled:   { cls: 'bg-gray-100 text-gray-600',     label: 'Annulé' },
                          }[item.payoutStatus] || { cls: 'bg-gray-100 text-gray-600', label: item.payoutStatus || '-' };
                          const estUnaf = item.type === 'virement_unaf';
                          return (
                            <tr key={`${item._id}-${index}`} className="text-sm bg-honey-50/40">
                              <td className="py-3 text-slate-600">{formatDate(item.datePaiement)}</td>
                              <td className="py-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-honey-100 text-honey-700">
                                  <ArrowsRightLeftIcon className="h-3 w-3 mr-1" />
                                  {estUnaf ? 'Virement UNAF' : 'Virement Stripe'}
                                </span>
                              </td>
                              <td className="py-3 text-slate-600 max-w-xs truncate">
                                {estUnaf
                                  ? `Export ${item.annee} — ${item.nombrePaiements} adhérent${item.nombrePaiements > 1 ? 's' : ''} · SAR`
                                  : `${item.description || 'Vers le compte bancaire'} · ${item.organisme}`}
                              </td>
                              <td className="py-3 text-right font-medium text-red-600">
                                -{formatMontant(item.montant)}
                              </td>
                              <td className="py-3 text-right text-slate-400">-</td>
                              <td className="py-3 text-right font-medium text-red-600">
                                -{formatMontant(item.montant)}
                              </td>
                              <td className="py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${payoutStatusBadge.cls}`}>
                                  {payoutStatusBadge.label}
                                </span>
                              </td>
                            </tr>
                          );
                        }

                        const frais = item.stripeFee ?? item.frais ?? null;
                        const net = frais != null
                          ? (item.montant || 0) - frais
                          : item.stripeNet ?? null;
                        return (
                          <tr key={`${item._id}-${index}`} className="text-sm">
                            <td className="py-3 text-slate-600">
                              {formatDate(item.datePaiement)}
                            </td>
                            <td className="py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                item.type === 'adhesion' ? 'bg-indigo-100 text-indigo-700' : 'bg-honey-100 text-honey-800'
                              }`}>
                                <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                                {item.type === 'adhesion' ? 'Adhésion' : 'Service'}
                              </span>
                            </td>
                            <td className="py-3 text-slate-600 max-w-xs truncate">
                              {item.type === 'adhesion'
                                ? `${item.organisme} — ${item.user?.prenom} ${item.user?.nom}`
                                : `${item.nom || 'Service'} — ${item.user?.prenom} ${item.user?.nom}`}
                            </td>
                            <td className="py-3 text-right font-medium text-green-600">
                              +{formatMontant(item.montant)}
                            </td>
                            <td className="py-3 text-right text-slate-500">
                              {frais != null ? `-${formatMontant(frais)}` : '-'}
                            </td>
                            <td className="py-3 text-right font-medium text-slate-900">
                              {net != null ? formatMontant(net) : formatMontant(item.montant)}
                            </td>
                            <td className="py-3">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Payé
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                      <p className="text-sm text-slate-500">
                        {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredPaiements.length)} sur {filteredPaiements.length} paiements
                      </p>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, idx) =>
                            p === '…' ? (
                              <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 text-sm">…</span>
                            ) : (
                              <button
                                key={p}
                                onClick={() => setCurrentPage(p)}
                                className={`min-w-[2rem] h-8 px-2 rounded-md text-sm font-medium transition-colors ${
                                  currentPage === p
                                    ? 'bg-slate-900 text-white'
                                    : 'text-slate-600 hover:bg-slate-100'
                                }`}
                              >
                                {p}
                              </button>
                            )
                          )}
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun paiement pour cette année</h3>
                  <p className="mt-1 text-sm text-gray-500">Aucun paiement enregistré pour {selectedYear}.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
